/**
 * English copy — the source of truth for the site's text.
 *
 * Marketing copy is lifted from "05 Landing Page.dc.html" and follows the
 * voice rules in "01 Brand Kit" and "09 Language Guide". Rewriting it here
 * would put the site out of step with the Instagram captions and the app, so
 * changes should go back into the brand documents too.
 *
 * Four voice rules that are easy to break by accident:
 *   Direct     — the number before the explanation. A figure beats an adjective.
 *   From here  — the real vocabulary, unquoted and unexplained.
 *   Honest     — name the trade and the limits before anyone discovers them.
 *   Guilt-free — never moralise about what someone spends on.
 *
 * "TwoCoins" is never translated and never broken into two words. "Plus" is
 * the plan name and stays in English too.
 *
 * The English text is the binding version of the notices everywhere except
 * Mexico, where the Spanish one is (LFPC art. 85) — see docs/legal.html.
 */
const en = {
  meta: {
    title: 'TwoCoins — the budget app that plans by paycheck',
    description:
      'What you can spend until your next paycheck, from the money you actually have: cards, bills and statement dates counted. No bank login. 30 days free.',
  },

  nav: {
    how: 'How it works',
    desktop: 'Desktop',
    gap: 'The gap',
    features: 'Features',
    pricing: 'Pricing',
    faq: 'Questions',
    // The landing page is the point of access now: this opens the app.
    cta: 'Open TwoCoins',
    skipToContent: 'Skip to content',
    menu: 'Menu',
    closeMenu: 'Close menu',
    languageLabel: 'Language',
  },

  hero: {
    badge: 'Built for the 15th and the 30th',
    headline: 'What you can spend until payday, from the money you actually have.',
    body: 'Your bank shows what is there. Not what the cards owe, the rent that leaves on the 1st, or the pay that lands on Friday. TwoCoins counts all four into one number that holds until your next payday, and warns you before a bill lands ahead of the pay that covers it. It never asks for your bank login.',
    ctaPrimary: 'Open TwoCoins',
    ctaSecondary: 'See how it works',
    ctaNote:
      'Opens in this browser. Thirty days of the whole app, no card, nothing to install unless you want to.',
    trust: ['30 days free, no card', 'Nothing to download', 'No bank login'],
    deskCap: {
      k: 'On a laptop',
      v: 'Backfill a week of expenses with a keyboard, in about four minutes.',
    },
    phoneCap: {
      k: 'On your phone',
      v: 'See what is free until payday, log it in two taps, signal or not.',
    },
  },

  /*
   * How it works — the section that replaces a download button.
   *
   * The argument is that a budgeting app you have to download before you can
   * judge it is asking for trust it has not earned.
   */
  pwa: {
    kicker: 'How it works',
    title:
      'It is a website. On your phone and on your laptop, until you decide it should be an app.',
    body: 'There is no store listing, because there does not need to be one. Open the link and the full app is running. Add it to your home screen when you want it one tap away, and it stops looking like a browser tab entirely.',
    steps: [
      {
        n: '1',
        k: 'Open the link',
        v: 'The complete app, running in your browser. Not a preview, not a demo account, not a video of an app.',
        art: 'twocoins.app',
        artK: 'Phone, laptop, anything with a browser',
      },
      {
        n: '2',
        k: 'Get your number',
        v: 'Five short questions: where you live, how often you are paid and how much, what is in the bank and on the cards, what repeats. Ninety seconds later you have a real figure.',
        art: '90 seconds',
        artK: 'Before we ask for anything',
      },
      {
        n: '3',
        k: 'Install it, if you want',
        v: 'Two taps on a phone, one click on a laptop. Own icon, own window, works with no signal, and it updates itself so you are never on an old version.',
        art: '2 taps',
        artK: 'Phone and desktop alike',
      },
    ],
    footK: 'Why it matters',
    foot: 'A budgeting app you have to download before you can judge it is asking for trust it has not earned. This one is useful in the first ninety seconds, on your data, on whatever phone you already have.',
  },

  desktopSection: {
    kicker: 'On a bigger screen',
    title: 'The same app, doing the jobs a phone is bad at.',
    body: 'Open the same link on a laptop and TwoCoins lays itself out for a keyboard. It is not a companion or a cut-down dashboard — it is the same ledger, the same numbers, the same account, arranged for the work you would never do with a thumb.',
    // The app's own page title and hint (strings.nav.activity, strings.desktop.entryRowHint).
    winTitle: 'Activity',
    winSub: 'Type, Tab across, Enter saves and opens the next one.',
    cols: ['Date', 'Description', 'Category', 'Account', 'Amount'],
    // Paid from checking: what you have, and the number, fall by it.
    draft: { d: 'Aug 20', n: 'Sweetgreen · downtown', c: 'Dining', a: 'Checking', v: '24' },
    rows: [
      {
        d: 'Aug 19',
        n: 'Trader Joe’s',
        c: 'Groceries',
        cc: '#2E8256',
        a: 'Checking',
        v: '$126',
        commit: false,
      },
      {
        d: 'Aug 18',
        n: 'Utilities · PG&E, internet',
        c: 'Committed',
        cc: '#3E2F7A',
        a: 'Checking',
        v: '$210',
        commit: true,
      },
      {
        d: 'Aug 17',
        n: 'Clipper · transit refill',
        c: 'Transit',
        cc: '#3A6FC4',
        a: 'Chase',
        v: '$40',
        commit: false,
      },
      {
        d: 'Aug 16',
        n: 'Netflix Premium',
        c: 'Committed',
        cc: '#3E2F7A',
        a: 'Chase',
        v: '$23',
        commit: true,
      },
      {
        d: 'Aug 16',
        n: 'Shell · fill-up',
        c: 'Transit',
        cc: '#3A6FC4',
        a: 'Chase',
        v: '$58',
        commit: false,
      },
      {
        d: 'Aug 15',
        n: 'Target · household',
        c: 'Shopping',
        cc: '#8A4E86',
        a: 'Checking',
        v: '$64',
        commit: false,
      },
    ],
    footK: 'Yours this cycle',
    cards: [
      {
        k: 'Backfill a week in four minutes',
        v: 'An entry row that never closes: type, Tab across, Enter saves and opens the next one. Or paste rows straight out of a bank statement and map the columns once.',
        art: '12 rows',
        artK: 'in under a minute',
      },
      {
        k: 'See every commitment end',
        v: 'Six months across the screen, one bar per obligation, each with the month it finally clears. The phone shows the total falling; only here do you see which line stops when.',
        art: '$480',
        artK: 'freed when the car clears',
      },
      {
        k: 'Charts worth the width',
        v: 'The spending curve across both halves of the cycle, your week day by day, what grew and what shrank, six months of category drift, and which card to put today on.',
        art: '6 months',
        artK: 'of drift at a glance',
      },
    ],
    foot: 'Set up at a desk and log on your phone — or never touch the phone at all: the laptop app is complete on its own, and nothing here needs a second device to finish.',
  },

  /*
   * The app in the hero, as Home draws it.
   *
   * One person's half-month — the US sample ledger with a paycheck every
   * other Friday into checking — worked out by the app's own arithmetic
   * (domain/money.ts). test/site-figures.test.ts runs that ledger through it
   * and holds every figure and date here to what comes out, so the page can
   * never quote a number the app would not show.
   */
  app: {
    greeting: 'Hi, Mariana',
    date: 'Thursday, Aug 20',
    period: 'August 16–31 · 11 days',
    freeLabel: 'Free until Aug 31',
    /** Under the number; the page fills in the amount as it moves. */
    perDay: 'About {amount} a day, today included',
    /** The days the number lasts, today included: what it is divided by. */
    days: 12,
    currency: 'USD',
    have: 'You have',
    owe: 'You owe',
    coming: 'Pay coming',
    due: 'Bills due',
    aheadLabel: 'Before Aug 31',
    /** Pay and bills after today, by date: positive comes in. */
    ahead: [
      { d: 'Aug 22', n: 'Claude Pro', v: -20, icon: 'spark' },
      { d: 'Aug 28', n: 'Paycheck', v: 2600, icon: 'arrowUp' },
      { d: 'Aug 28', n: 'Spotify Duo', v: -16.99, icon: 'play' },
    ],
    /**
     * The settled figures, before the expense typed in the window lands. It is
     * paid from checking, so it comes off what you have and off the number.
     * `spent` is what was freely spent this cycle, the table's foot.
     */
    sim: { free: 3033.01, have: 2310, owe: 1840, coming: 2600, due: 36.99, spent: 640 },
    // The desktop app's own labels, for the window in the hero.
    nav: ['Home', 'Activity', 'Money'],
    navMore: ['You', 'Help'],
    addExpense: 'Add expense',
    search: 'Search',
  },

  marquee: [
    'Semi-monthly cycles',
    'Statement close dates',
    'Pay that lands by itself',
    'Phone and laptop',
    'Shared costs',
    'No bank login',
  ],

  gap: {
    kicker: 'The gap',
    title: 'Every budgeting app was designed for someone else’s banking system.',
    body: 'They hand you an allowance worked out from one monthly paycheck — pay, minus bills, minus what you logged — and never look at what is in your account. So the number says you are fine the week the card bill empties your checking, and says you are short with three thousand sitting in it.',
    themTitle: 'A generic tracker',
    them: [
      'Gives you an allowance from your pay, without looking at your bank.',
      'Assumes one paycheck a month, on a date it picks.',
      'Wants your online-banking password to “help”.',
      'Has no idea your statement closes on the 12th.',
    ],
    us: [
      'Starts from what is in your accounts, less what the cards owe.',
      'Counts to your next payday: the 15th, the 30th, every other Friday.',
      'Never asks. You type the merchant once; it remembers.',
      'Tells you which card gives you 48 days of float today.',
    ],
  },

  features: {
    kicker: 'What it does',
    title: 'Five things no other app here gets right.',
    items: [
      {
        kicker: 'The number',
        title: 'What is free until payday, from what is really in your account.',
        body: 'Most budgeting apps hand you an allowance worked out from your pay, and never look at your bank. TwoCoins starts from what your accounts hold, takes off what the cards owe and every bill still to land before the period ends, adds the pay still coming, and gives you one figure, with what it comes to a day. Tap it and every line is there.',
        artLabel: 'Free until Aug 31',
        artValue: '$3,033.01',
        /** The working, in the app's words: the hero's ledger, line by line. */
        artSum: [
          { k: 'You have', v: '$2,310.00' },
          { k: 'Cards owe', v: '−$1,840.00' },
          { k: 'Pay coming', v: '+$2,600.00' },
          { k: 'Bills due', v: '−$36.99' },
        ],
        artNote: 'About $252.75 a day, today included.',
      },
      {
        kicker: 'Balances that stay true',
        title: 'Pay lands on payday. Paying a card is not spending twice.',
        body: 'Tell TwoCoins when you are paid and the pay lands in your account on the day, by itself: every other Friday, the 15th and the last day, or the Friday before a payday that falls on a Sunday. Every bill you set up posts itself the same way. Paying a card moves money from checking to the card instead of counting as spending, and when your bank says otherwise, you fix the balance in one step.',
        artLabel: 'Checking · Aug 28',
        artValue: '+$2,600.00',
        artNote: 'Your paycheck, in on its day by itself. Nothing to type.',
      },
      {
        kicker: 'Before you come up short',
        title: 'It warns you when a bill lands before the pay that covers it.',
        body: 'A half-month can add up and still leave you short for two days. TwoCoins walks your money a day at a time — what is in the bank, each bill on its day, each payday — and when rent leaves before your pay arrives, Home says so, with the date and the amount, while there is still time to move something.',
        artLabel: 'Heads-up',
        artValue: '$550.00',
        artNote: 'On Aug 26 you would be $550.00 short, before your pay on Aug 28.',
      },
      {
        kicker: 'Statement close',
        title: 'The right card today is the one that just closed.',
        body: 'TwoCoins knows every card’s closing and due date. Buy the day after a statement closes and you don’t pay for it for another seven weeks. That is free float nobody tells you about. It also tracks your limit, so you can see utilisation before it hits your credit score.',
        artLabel: 'Best card today',
        artValue: '48 days',
        artNote:
          'Banorte Oro just closed its statement, so today’s charge lands on next month’s bill.',
      },
      {
        kicker: 'Shared costs',
        title: 'Share the rent, not your account.',
        body: 'Log what you share with a partner or a roommate, whoever paid it: your card, theirs, or half each at the till. Record what they hand you, and split by what each of you can afford that month. Your numbers count only your part, and what you front stays out of what is free until it comes back. They never need an account.',
        artLabel: 'Ana owes you',
        artValue: '$190',
        /** Your part of the split, for the bar under the figure. */
        artSplit: 0.6,
        artNote: 'Split 60/40. Of the $1,600 rent, $1,200 counts as yours; Ana paid her $400.',
      },
    ],
  },

  privacy: {
    kicker: 'Privacy',
    title: 'We never ask for your bank password. There isn’t a field for it.',
    body: 'You type what you spend, or pick it from the stores you’ve used before. It takes four seconds and it means your bank login is never anywhere it could leak. No screen-scraping, no aggregator holding your credentials.',
    body2:
      'There is nothing to download. It opens in your browser, and you can add it to your home screen when you want it one tap away. Lose signal and it keeps working, so an error is never a lost expense.',
    points: [
      {
        k: 'No credentials, ever',
        v: 'There’s no bank-login screen because there’s no bank connection.',
      },
      {
        k: 'In your account',
        v: 'Your expenses are saved to your account and follow you to every device, with a copy on each so the app works offline. We never sell them.',
      },
      {
        k: 'Offline by default',
        v: 'Log a coffee on the metro with no signal. It queues and syncs later.',
      },
      {
        k: 'Nothing to sell',
        v: 'No ad network, no data partners, no “anonymised insights” business.',
      },
    ],
  },

  pricing: {
    kicker: 'Pricing',
    title: 'One price. The whole application, on every screen you own.',
    sub: 'Not a phone app with a website attached. One subscription covers the phone, the desktop ledger, the commitments timeline and the charts, on as many of your own devices as you like. There is no cut-down free tier, because a budgeting app that hides half your commitments is a demo that lies to you — you get all of it for thirty days instead, which is two full pay cycles, and no card is asked for until the end.',

    monthlyName: 'Monthly',
    monthlyPrice: '$9.99',
    monthlyPer: 'USD a month',
    monthlyBody: 'Phone and desktop, every feature, every device you use. Cancel any month.',

    yearlyName: 'Yearly',
    yearlyPrice: '$99',
    yearlyPer: 'USD a year',
    yearlyBadge: '2 months free',
    yearlyBody:
      'The same complete app, two months cheaper — for when you already know you are staying.',

    cta: 'Start 30 days free',

    /*
     * What it is worth, in the user's own money.
     *
     * Every figure here is one the app computes from a real ledger elsewhere in
     * the product — the interest on a minimum payment, the amount a cleared loan
     * gives back. Stating the price beside them is the argument.
     */
    payK: 'What it is worth',
    payTitle: 'It pays for itself the first time it stops you paying a minimum.',
    payStats: [
      {
        v: '$14',
        k: 'the interest one minimum payment costs you next month — from the example on this page',
      },
      { v: '$480', k: 'a month back when the car loan finishes, if you know which month that is' },
      {
        v: '$9.99',
        k: 'US dollars a month for the whole application — less than one streaming subscription',
      },
    ],

    inclTitle: 'Every plan is the whole application. There is nothing above it to buy.',
    includes: [
      'Unlimited accounts and cards, with balances that keep themselves up to date',
      'Every recurring bill, posted on its day, with the month each one finally clears',
      'Half-month, weekly, monthly and custom cycles',
      'Statement close and due dates for every card',
      'Loans and car credit, with the interest stated separately',
      'The desktop ledger table and keyboard backfill',
      'Trends, category limits and comparisons, and CSV export whenever you want out',
      'Works offline, on phone and laptop, installed or not',
      'One subscription, every device you own — no second purchase',
    ],

    note: 'No card to start the thirty days. When they end, the app asks whether you want to subscribe, and nothing is charged unless you do. Cancel and your data stays exactly where it is — the app locks, nothing is deleted, and export is always one tap.',
  },

  faq: {
    kicker: 'Questions',
    title: 'The ones we actually get.',
    items: [
      {
        q: 'Where do I download it?',
        a: 'You don’t. TwoCoins is a website that behaves like an app: open the link and you are using it. If you want it on your home screen, add it in two taps and it opens full screen, works offline, and updates itself. There is no store listing and no install to sit through first.',
      },
      {
        q: 'Do you connect to my bank?',
        a: 'No, and we don’t plan to. You add expenses yourself, which takes a few seconds and keeps your credentials out of the picture entirely. The trade is real: you do a little typing, and nobody holds your banking password.',
      },
      {
        q: 'What exactly is a commitment to the app?',
        a: 'Anything with an amount and a day it leaves: rent, the car payment, insurance, every subscription. Each one that lands before the period ends comes off what is free, sits in the list of what is due by date, and posts itself on its day, so you never type it twice.',
      },
      {
        q: 'What if I’m freelance, paid by the job, or have two jobs?',
        a: 'Add each way you’re paid. Pay on set days counts on its days and lands in its account by itself — a weekly wage beside a monthly one, each in its place, and a payday on a weekend shown on the day it really comes. Pay that varies counts once it arrives, never before, so the number never spends work that hasn’t paid yet; if you invoice before tax, the share you hold back stays set aside. Whatever is left is simply in your account next cycle, because the number starts from what is there.',
      },
      {
        q: 'Does it work outside the US?',
        a: 'Yes. Switch the market to Mexico and the app adds what applies there — interest-free instalment plans and corte dates — because that is how credit actually works in that market. We would rather add a real feature than pretend one market is the other.',
      },
      {
        q: 'Can I track a car loan?',
        a: 'Yes. Monthly payment, rate, payments made, balance left, and what the interest is actually costing you each month, stated separately, because interest is the part you never get back.',
      },
      {
        q: 'Can I share expenses with my partner or a roommate?',
        a: 'Yes. Keep a shared tab with one person — they don’t need an account. Log what you share whoever paid, record what they hand you, and split by what each of you can afford that month. Your categories, limits and what is free count only your part.',
      },
      {
        q: 'Can I start fresh without losing my account?',
        a: 'Yes. In Data and privacy you can clean your records — all of them, or everything before a date — and keep your accounts and their balances, cards, categories and commitments, or start over as if the account were new. Your plan and your trial days stay either way.',
      },
      {
        q: 'Is there a free version?',
        a: 'No, and that is deliberate. Every free budgeting tier works by hiding something — a second account, the commitments timeline, the cycle that matches your payday — and a partial picture of your money is worse than no picture, because you trust it. You get the whole app for thirty days instead, which is two full pay cycles, without giving us a card.',
      },
      {
        q: 'What happens if I stop paying?',
        a: 'The app locks and nothing is deleted. Your cards, categories and history stay saved in your account, and CSV export keeps working. Start paying again and everything is where you left it.',
      },
      {
        q: 'Who’s building this?',
        a: 'Build Passing. Small team, no investors telling us to add a crypto tab.',
      },
    ],
  },

  finalCta: {
    title: 'Find out what’s actually yours until payday.',
    body: 'Five short questions, ninety seconds, and a number that starts from what is in your account.',
    cta: 'Open TwoCoins',
    note: 'Thirty days free. No card to start — just your email.',
  },

  footer: {
    tagline: 'A budgeting app built on how credit actually works here.',
    builtBy: 'Built by',
    columns: { product: 'Product', legal: 'Legal', company: 'Company' },
    links: {
      how: 'How it works',
      desktop: 'Desktop',
      gap: 'The gap',
      features: 'Features',
      pricing: 'Pricing',
      faq: 'Questions',
      guides: 'Guides',
      openApp: 'Open TwoCoins',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Policy',
      deleteAccount: 'Delete your account',
      support: 'Support',
      contact: 'Contact',
      instagram: 'Instagram',
      buildpassing: 'BuildPassing',
    },
    rights: 'All rights reserved.',
  },

  notFound: {
    title: 'That page isn’t here.',
    body: 'The link may be out of date, or we may have moved it. The front page is a good place to start.',
    cta: 'Back to the front page',
  },

  /*
   * The guides' own labels. Each guide's text lives in src/client/guides —
   * it is content, not interface — and these are the words around it.
   */
  guides: {
    title: 'Guides',
    intro:
      'Plain answers on how money actually moves: paycheck to paycheck, statement to statement. Each one teaches first, with the arithmetic shown; the app comes up in the last paragraph, if at all.',
    read: 'Read the guide',
    contents: 'In this guide',
    trail: 'You are here',
    updated: 'Updated',
    ctaTitle: 'Let the app keep the numbers',
    ctaBody:
      'TwoCoins does this arithmetic every cycle, with your own bills and cards. Thirty days free, no card to start, and never your bank login.',
    ctaButton: 'Open TwoCoins',
    more: 'More guides',
  },

  legal: {
    lastUpdated: 'Last updated',
    effectiveDate: 'Effective date',
    contents: 'Contents',
    questionsTitle: 'Questions about this document?',
    questionsBody: 'Write to us and a person will answer.',
    bindingNotice:
      'This document is also available in Spanish. If you live in Mexico, the Spanish version governs; anywhere else, the English version does.',
  },
};

/* Deliberately not `as const`. A literal type here would force the Spanish
   bundle to repeat the English strings exactly, which is the opposite of what
   we want — we need the same *shape*, not the same words. */
export default en;
